import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCompanyRequest,
  getSellerRequest,
  getCompanyWarehousesRequest,
  getCompanyOperatorsRequest,
  getCompanyIntegrationsRequest,
  changeSellerPasswordRequest,
  downloadSellerWarehousesRequest,
  getCompanySettingCouriersRequest,
  createCompanySettingCourierRequest,
  updateCompanySettingCourierRequest,
  deleteCompanySettingCourierRequest,
  getAvailableCouriersRequest,
  updateWarehousePickupScheduleRequest,
} from './sellersApi';
import { getImagesUrls } from '../../utils/s3Utils';
import { capitalizeWords, generateRandomToken } from '../../utils/functions';

export const fetchCompanyDetails = createAsyncThunk(
  'sellerDetails/fetchCompanyDetails',
  async (companyId, { rejectWithValue }) => {
    if (!companyId) return rejectWithValue('Company ID no provisto');

    try {
      const [
        companyResponse,
        sellerResponse,
        warehousesResponse,
        operatorsResponse,
        settingEcommerceResponse,
      ] = await Promise.all([
        getCompanyRequest(companyId),
        getSellerRequest(companyId),
        getCompanyWarehousesRequest(companyId),
        getCompanyOperatorsRequest(companyId),
        getCompanyIntegrationsRequest(companyId),
      ]);

      const imageskeyRoutes = {
        s3Woocommerce: "assets/images/woocommerce.png",
        s3Shopify: "assets/images/shopify.png",
        s3MercadoLibre: "assets/images/mercadolibre.png",
        s3Bootic: "assets/images/bootic.png",
        s3Prestashop: "assets/images/prestashop.png",
        s3Vtex: "assets/images/vtex.png",
      };

      const imageUrls = await getImagesUrls(imageskeyRoutes, "ecommerces");
      const rawIntegrations = settingEcommerceResponse.data || [];

      const settingEcommerces = rawIntegrations.map((ecommerce) => {
        const keyName = `s3${capitalizeWords(ecommerce.name).split(" ").join("")}`;
        ecommerce.image = imageUrls[keyName] || imageUrls['s3Woocommerce']; // fallback
        return ecommerce;
      });

      // Backend retorna un arreglo para el seller, tomamos el primero
      const seller = (sellerResponse.data && sellerResponse.data[0]) || {};

      const companyData = {
        id: companyResponse.data.id,
        rut: companyResponse.data.rut,
        fullName: `${seller?.user?.first_name || ''} ${seller?.user?.last_name || ''}`.trim(),
        exposedApiToken: companyResponse.data.exposed_api_token,
        socialReason: companyResponse.data.social_reason,
        kam: companyResponse.data.kam?.user?.email || "Sin KAM",
        createdAt: companyResponse.data.created_at,
        lastLogin: seller?.user?.last_login,
        plan: companyResponse.data.plan,
        seller: seller,
        warehouses: warehousesResponse.data.serialized_warehouses || [],
        operators: operatorsResponse.data.serialized_operators || [],
        setting_ecommerces: settingEcommerces || [],
      };

      return companyData;
    } catch (error) {
      console.error('Error al obtener datos de la compañia:', error);
      return rejectWithValue('Error al obtener datos de la compañia');
    }
  }
);

export const changeSellerPassword = createAsyncThunk(
  'sellerDetails/changeSellerPassword',
  async ({ sellerId, tempPassword }, { rejectWithValue }) => {
    try {
      const payload = {
        new_password1: tempPassword,
        new_password2: tempPassword,
        token: generateRandomToken(14),
      };
      await changeSellerPasswordRequest(sellerId, payload);
      return true;
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error);
      return rejectWithValue('Error al cambiar la contraseña del seller');
    }
  }
);

export const requestDownloadWarehouses = createAsyncThunk(
  'sellerDetails/requestDownloadWarehouses',
  async (sellerId, { rejectWithValue }) => {
    try {
      const response = await downloadSellerWarehousesRequest(sellerId);
      return response.data;
    } catch (error) {
      console.error('Error al solicitar descarga de bodegas:', error);
      return rejectWithValue('No se iniciar la descarga de bodegas');
    }
  }
);

/* -------------------------------------------------------------------------- */
/* Courier settings, from the courier section of the legacy `CompanyDetail`   */
/* -------------------------------------------------------------------------- */

/**
 * The configuration to send for a setting courier: its current flags with some
 * of them changed. The backend replaces `configuration` whole, so a flag left
 * out would be switched off — the legacy sent both on every toggle.
 *
 * @param {object} settingCourier - As the backend answers it.
 * @param {object} changes - e.g. `{ stand_by_enabled: true }`.
 */
export const buildSettingCourierConfiguration = (settingCourier, changes = {}) => ({
  stand_by_enabled: settingCourier?.configuration?.stand_by_enabled ?? false,
  requires_pickup_requests: settingCourier?.configuration?.requires_pickup_requests ?? false,
  ...changes,
});

export const fetchSellerCouriers = createAsyncThunk(
  'sellerDetails/fetchSellerCouriers',
  async (companyId, { rejectWithValue }) => {
    try {
      const { data } = await getCompanySettingCouriersRequest(companyId);
      return data || [];
    } catch (error) {
      console.error('Error al cargar configuración de couriers:', error);
      return rejectWithValue(null);
    }
  }
);

export const fetchAvailableCouriers = createAsyncThunk(
  'sellerDetails/fetchAvailableCouriers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getAvailableCouriersRequest();
      return data || [];
    } catch (error) {
      console.error('Error al cargar couriers disponibles:', error);
      return rejectWithValue(null);
    }
  }
);

/**
 * @param {{companyId: number, courier: string, configuration: object}} params
 */
export const createSellerCourier = createAsyncThunk(
  'sellerDetails/createSellerCourier',
  async ({ companyId, courier, configuration }, { rejectWithValue }) => {
    try {
      const { data } = await createCompanySettingCourierRequest(companyId, courier, configuration);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.setting_courier ||
        error.response?.data?.courier?.[0] ||
        'Error al crear la configuración';
      return rejectWithValue(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }
);

/**
 * @param {{companyId: number, settingCourierId: number, configuration: object, errorMessage?: string}} params
 *   `configuration` must carry every flag; see `buildSettingCourierConfiguration`.
 */
export const updateSellerCourier = createAsyncThunk(
  'sellerDetails/updateSellerCourier',
  async ({ companyId, settingCourierId, configuration, errorMessage }, { rejectWithValue }) => {
    try {
      const { data } = await updateCompanySettingCourierRequest(companyId, settingCourierId, configuration);
      return data;
    } catch (error) {
      return rejectWithValue(errorMessage || 'Error al actualizar la configuración');
    }
  },
  {
    // A row has two switches: a caller that names the `flag` it flipped gets
    // only that switch spinning.
    getPendingMeta: ({ arg }) => ({
      pendingKey: arg.flag ? `${arg.settingCourierId}:${arg.flag}` : arg.settingCourierId,
    }),
  }
);

/**
 * The hours a courier may collect at one of the seller's warehouses.
 *
 * Resolves with the warehouse the backend answers, which is the whole record:
 * the slice puts it back in `company.warehouses` by id.
 *
 * @param {{warehouseId: number, schedule: object}} params - See `buildPickupSchedulePayload`.
 */
export const updateWarehousePickupSchedule = createAsyncThunk(
  'sellerDetails/updateWarehousePickupSchedule',
  async ({ warehouseId, schedule }, { rejectWithValue }) => {
    try {
      const { data } = await updateWarehousePickupScheduleRequest(warehouseId, schedule);
      return data;
    } catch (error) {
      console.error('Error al actualizar el horario de retiro:', error);
      return rejectWithValue('No se pudo actualizar el horario de retiro');
    }
  }
);

/**
 * @param {{companyId: number, settingCourierId: number}} params
 */
export const deleteSellerCourier = createAsyncThunk(
  'sellerDetails/deleteSellerCourier',
  async ({ companyId, settingCourierId }, { rejectWithValue }) => {
    try {
      await deleteCompanySettingCourierRequest(companyId, settingCourierId);
      return settingCourierId;
    } catch (error) {
      return rejectWithValue('Error al eliminar la configuración');
    }
  }
);
